import { cloneElement, isValidElement, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/cn';

// ── Botón ───────────────────────────────────────────────────

type VarianteBoton = 'primario' | 'secundario' | 'peligro' | 'fantasma';

export function Boton({
  variante = 'primario',
  cargando = false,
  icono,
  children,
  className,
  ...props
}: {
  variante?: VarianteBoton;
  cargando?: boolean;
  icono?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const estilos: Record<VarianteBoton, string> = {
    primario: 'bg-kick-red text-white hover:bg-kick-red-dark',
    secundario: 'border border-kick-gray/30 bg-white text-kick-ink hover:bg-kick-light',
    peligro: 'border border-red-300 bg-white text-red-600 hover:bg-red-50',
    fantasma: 'text-kick-gray hover:bg-kick-light hover:text-kick-ink',
  };

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-kick px-4 py-2.5 text-sm font-semibold transition',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kick-red',
        'disabled:cursor-not-allowed disabled:opacity-50',
        estilos[variante],
        className,
      )}
      disabled={cargando || props.disabled}
      {...props}
    >
      {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : icono}
      {children}
    </button>
  );
}

// ── Campos de formulario ────────────────────────────────────

/**
 * Etiqueta un campo de formulario.
 *
 * Asocia la etiqueta al control automáticamente: le inyecta un `id` al hijo si
 * no lo trae. Sin eso, el lector de pantalla no anuncia el campo y hacer clic
 * en el texto de la etiqueta no lo enfoca.
 */
export function Campo({
  etiqueta,
  error,
  ayuda,
  requerido,
  children,
  className,
}: {
  etiqueta: string;
  error?: string;
  ayuda?: string;
  requerido?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const idGenerado = useId();
  const idAyuda = `${idGenerado}-ayuda`;

  // Si el control ya trae su propio id se respeta; si no, se le pone el nuestro.
  const control = isValidElement<{
    id?: string;
    'aria-describedby'?: string;
    'aria-required'?: boolean;
  }>(children)
    ? cloneElement(children, {
        id: children.props.id ?? idGenerado,
        'aria-describedby': error || ayuda ? idAyuda : undefined,
        // `aria-required` en vez del atributo `required` nativo: obligatorio
        // se anuncia igual, pero el navegador no impone su propia validación
        // encima de los mensajes del formulario.
        ...(requerido ? { 'aria-required': true } : {}),
      })
    : children;

  const idControl = isValidElement<{ id?: string }>(children)
    ? (children.props.id ?? idGenerado)
    : idGenerado;

  return (
    <div className={className}>
      <label htmlFor={idControl} className="mb-1.5 block text-sm font-semibold text-kick-ink">
        {etiqueta}
        {/*
          El asterisco es decorativo: se oculta al lector de pantalla para que
          el campo se llame «Nombre» y no «Nombre asterisco». Que sea
          obligatorio ya lo dice `aria-required` en el control.
        */}
        {requerido && (
          <span aria-hidden className="ml-0.5 text-kick-red">
            *
          </span>
        )}
      </label>
      {control}
      {error ? (
        <p id={idAyuda} className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      ) : (
        ayuda && (
          <p id={idAyuda} className="mt-1.5 text-xs text-kick-gray">
            {ayuda}
          </p>
        )
      )}
    </div>
  );
}

const claseCampo =
  'w-full rounded-kick border bg-white px-3.5 py-2.5 text-kick-ink outline-none transition ' +
  'focus:border-kick-red focus:ring-2 focus:ring-kick-red/20 disabled:bg-kick-light';

export function Entrada({
  error,
  className,
  ...props
}: { error?: boolean } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(claseCampo, error ? 'border-red-400' : 'border-kick-gray/30', className)}
      {...props}
    />
  );
}

export function AreaTexto({
  error,
  className,
  ...props
}: { error?: boolean } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      rows={3}
      className={cn(
        claseCampo,
        'resize-y',
        error ? 'border-red-400' : 'border-kick-gray/30',
        className,
      )}
      {...props}
    />
  );
}

export function Seleccion({
  error,
  className,
  children,
  ...props
}: { error?: boolean } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(claseCampo, error ? 'border-red-400' : 'border-kick-gray/30', className)}
      {...props}
    >
      {children}
    </select>
  );
}

/**
 * Campo de dinero.
 *
 * El usuario escribe pesos ("12.500") y el componente entrega centavos
 * (1250000), que es como viaja el dinero en toda la plataforma.
 */
export function EntradaDinero({
  centavos,
  onCambio,
  error,
  ...props
}: {
  centavos: number;
  onCambio: (centavos: number) => void;
  error?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [texto, setTexto] = useState(() => (centavos ? String(Math.round(centavos / 100)) : ''));

  useEffect(() => {
    const pesos = centavos ? String(Math.round(centavos / 100)) : '';
    // Solo se sincroniza si el valor externo cambió de verdad, para no
    // interrumpir lo que el usuario está escribiendo.
    setTexto((actual) => (Number(actual || 0) === Number(pesos || 0) ? actual : pesos));
  }, [centavos]);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-kick-gray">
        $
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={texto ? Number(texto).toLocaleString('es-CO') : ''}
        onChange={(e) => {
          const soloDigitos = e.target.value.replace(/\D/g, '');
          setTexto(soloDigitos);
          onCambio(soloDigitos ? Number(soloDigitos) * 100 : 0);
        }}
        className={cn(claseCampo, 'pl-7', error ? 'border-red-400' : 'border-kick-gray/30')}
        {...props}
      />
    </div>
  );
}

export function Interruptor({
  activo,
  onCambio,
  etiqueta,
  disabled,
}: {
  activo: boolean;
  onCambio: (v: boolean) => void;
  etiqueta: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label={etiqueta}
      disabled={disabled}
      onClick={() => onCambio(!activo)}
      className={cn(
        'relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-50',
        activo ? 'bg-emerald-500' : 'bg-kick-gray/40',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all',
          activo ? 'left-[1.375rem]' : 'left-0.5',
        )}
      />
    </button>
  );
}

// ── Modal ───────────────────────────────────────────────────

export function Modal({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  ancho = 'md',
  children,
}: {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  descripcion?: string;
  ancho?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}) {
  // Escape cierra el modal y el fondo no se desplaza mientras está abierto.
  useEffect(() => {
    if (!abierto) return;
    const alPresionar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alPresionar);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPresionar);
      document.body.style.overflow = '';
    };
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  const anchos = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl' };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={cn('w-full rounded-kick bg-white shadow-xl animate-slide-up', anchos[ancho])}
      >
        <div className="flex items-start justify-between gap-4 border-b border-kick-gray/15 p-5">
          <div>
            <h2 className="text-xl text-kick-dark">{titulo}</h2>
            {descripcion && <p className="mt-0.5 text-sm text-kick-gray">{descripcion}</p>}
          </div>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className="rounded-kick p-1.5 text-kick-gray transition hover:bg-kick-light hover:text-kick-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ── Estados de carga y vacío ────────────────────────────────

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-kick-gray">
      <Loader2 className="h-5 w-5 animate-spin" />
      {texto}
    </div>
  );
}

export function Vacio({
  icono,
  titulo,
  descripcion,
  accion,
}: {
  icono: React.ReactNode;
  titulo: string;
  descripcion: string;
  accion?: React.ReactNode;
}) {
  return (
    <div className="rounded-kick border border-dashed border-kick-gray/30 px-6 py-12 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-kick-light text-kick-gray">
        {icono}
      </div>
      <h3 className="mt-4 text-lg text-kick-dark">{titulo}</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-kick-gray">{descripcion}</p>
      {accion && <div className="mt-5">{accion}</div>}
    </div>
  );
}

export function ErrorCarga({ mensaje, onReintentar }: { mensaje: string; onReintentar?: () => void }) {
  return (
    <div className="rounded-kick border border-red-200 bg-red-50 p-6 text-center">
      <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
      <p className="mt-3 font-semibold text-red-800">No pudimos cargar esta información</p>
      <p className="mt-1 text-sm text-red-600">{mensaje}</p>
      {onReintentar && (
        <Boton variante="secundario" onClick={onReintentar} className="mt-4">
          Reintentar
        </Boton>
      )}
    </div>
  );
}

/** Confirmación para acciones destructivas. */
export function ConfirmarModal({
  abierto,
  titulo,
  mensaje,
  textoConfirmar = 'Eliminar',
  cargando,
  onConfirmar,
  onCancelar,
}: {
  abierto: boolean;
  titulo: string;
  mensaje: string;
  textoConfirmar?: string;
  cargando?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}) {
  return (
    <Modal abierto={abierto} onCerrar={onCancelar} titulo={titulo} ancho="sm">
      <div className="p-5">
        <p className="text-kick-ink">{mensaje}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCancelar}>
            Cancelar
          </Boton>
          <Boton variante="peligro" cargando={cargando} onClick={onConfirmar}>
            {textoConfirmar}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
